import React from 'react';
import PropTypes from 'prop-types';
import { htmlToPlainText } from '../utilities/htmlToPlainText';

const TEXT_NODE = 3;
const ELEMENT_NODE = 1;

const DROPPED_TAGS = new Set([
  'applet',
  'base',
  'embed',
  'frame',
  'frameset',
  'iframe',
  'link',
  'math',
  'meta',
  'noscript',
  'object',
  'script',
  'style',
  'svg',
  'template',
  'textarea',
  'title',
]);

const ALLOWED_TAGS = {
  a: 'a',
  b: 'b',
  blockquote: 'blockquote',
  br: 'br',
  caption: 'caption',
  center: 'center',
  code: 'code',
  col: 'col',
  colgroup: 'colgroup',
  del: 'del',
  div: 'div',
  em: 'em',
  font: 'font',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  hr: 'hr',
  i: 'i',
  img: 'img',
  ins: 'ins',
  li: 'li',
  ol: 'ol',
  p: 'p',
  pre: 'pre',
  s: 's',
  small: 'small',
  span: 'span',
  strike: 'strike',
  strong: 'strong',
  sub: 'sub',
  sup: 'sup',
  table: 'table',
  tbody: 'tbody',
  td: 'td',
  tfoot: 'tfoot',
  th: 'th',
  thead: 'thead',
  tr: 'tr',
  u: 'u',
  ul: 'ul',
};

const VOID_TAGS = new Set(['br', 'col', 'hr', 'img']);

const ATTRIBUTE_MAP = {
  align: 'align',
  alt: 'alt',
  border: 'border',
  cellpadding: 'cellPadding',
  cellspacing: 'cellSpacing',
  class: 'className',
  color: 'color',
  colspan: 'colSpan',
  dir: 'dir',
  face: 'face',
  height: 'height',
  lang: 'lang',
  rowspan: 'rowSpan',
  size: 'size',
  start: 'start',
  title: 'title',
  valign: 'valign',
  width: 'width',
};

const ALLOWED_STYLE_PROPERTIES = new Set([
  'background-color',
  'border',
  'color',
  'display',
  'font-family',
  'font-size',
  'font-style',
  'font-weight',
  'height',
  'line-height',
  'list-style-type',
  'margin',
  'margin-bottom',
  'margin-left',
  'margin-right',
  'margin-top',
  'padding',
  'padding-left',
  'text-align',
  'text-decoration',
  'vertical-align',
  'white-space',
  'width',
]);

const UNSAFE_CSS_VALUE =
  /expression|javascript|vbscript|mocha:|livescript:|@import|url\s*\(|-moz-binding|behavior\s*:|<|>/i;

const SAFE_LINK_TARGETS = new Set(['_blank', '_self', '_parent', '_top']);

const UNSAFE_URL_SCHEMES = [
  'javascript:',
  'vbscript:',
  'data:',
  'blob:',
  'livescript:',
  'mocha:',
];

const hasUnsafeUrlScheme = (value) => {
  const normalized = String(value)
    .replace(/[\u0000-\u0020]+/g, '')
    .toLowerCase();

  return UNSAFE_URL_SCHEMES.some((scheme) => normalized.startsWith(scheme));
};

const isSafeCssValue = (value) => {
  if (UNSAFE_CSS_VALUE.test(value)) {
    return false;
  }

  const withoutColorFunctions = value
    .replace(/rgba?\([^)]*\)/gi, '')
    .replace(/hsla?\([^)]*\)/gi, '');

  return (
    !withoutColorFunctions.includes('(') && !withoutColorFunctions.includes(')')
  );
};

const toStyleObject = (cssText) => {
  const style = {};

  cssText.split(';').forEach((declaration) => {
    const separatorIndex = declaration.indexOf(':');
    if (separatorIndex === -1) {
      return;
    }

    const property = declaration.slice(0, separatorIndex).trim().toLowerCase();
    const value = declaration.slice(separatorIndex + 1).trim();

    if (
      !ALLOWED_STYLE_PROPERTIES.has(property) ||
      !value ||
      !isSafeCssValue(value)
    ) {
      return;
    }

    const reactProperty = property.replace(/-([a-z])/g, (_, character) =>
      character.toUpperCase()
    );
    style[reactProperty] = value;
  });

  return Object.keys(style).length ? style : null;
};

const buildProps = (node, tagName, key) => {
  const props = { key };

  Array.from(node.attributes).forEach((attribute) => {
    const name = attribute.name.toLowerCase();
    const value = attribute.value;

    if (name.startsWith('on') || name === 'srcdoc' || name === 'formaction') {
      return;
    }

    if (name === 'style') {
      const style = toStyleObject(value);
      if (style) {
        props.style = style;
      }
      return;
    }

    if (name === 'href' || name === 'src') {
      if (value && !hasUnsafeUrlScheme(value)) {
        props[name] = value;
      }
      return;
    }

    if (name === 'target' && tagName === 'a') {
      const target = value.toLowerCase();
      if (SAFE_LINK_TARGETS.has(target)) {
        props.target = target;
      }
      return;
    }

    const reactName = ATTRIBUTE_MAP[name];
    if (reactName) {
      props[reactName] = value;
    }
  });

  if (tagName === 'a' && props.href) {
    props.rel = 'noopener noreferrer';
  }

  return props;
};

const flattenNodes = (nodes) =>
  nodes.reduce((accumulated, node) => {
    if (node == null || node === '') {
      return accumulated;
    }
    if (Array.isArray(node)) {
      return accumulated.concat(flattenNodes(node));
    }
    accumulated.push(node);
    return accumulated;
  }, []);

const toReactNode = (node, key) => {
  if (node.nodeType === TEXT_NODE) {
    return node.textContent;
  }

  if (node.nodeType !== ELEMENT_NODE) {
    return null;
  }

  const tagName = node.tagName.toLowerCase();
  if (DROPPED_TAGS.has(tagName)) {
    return null;
  }

  const children = flattenNodes(
    Array.from(node.childNodes).map((child, index) =>
      toReactNode(child, `${key}.${index}`)
    )
  );
  const safeTag = ALLOWED_TAGS[tagName];

  if (!safeTag) {
    return children;
  }

  const props = buildProps(node, safeTag, key);
  if (safeTag === 'a' && !props.href) {
    return children;
  }

  if (VOID_TAGS.has(safeTag)) {
    return React.createElement(safeTag, props);
  }

  return React.createElement(safeTag, props, children);
};

const renderSafeHtml = (html) => {
  if (typeof DOMParser === 'undefined') {
    return htmlToPlainText(html);
  }

  const document = new DOMParser().parseFromString(html, 'text/html');
  return flattenNodes(
    Array.from(document.body.childNodes).map((node, index) =>
      toReactNode(node, `html-${index}`)
    )
  );
};

const SafeHtmlContent = ({ html, ...rest }) => {
  if (html == null || typeof html !== 'string' || !html.trim()) {
    return null;
  }

  const safeRest = { ...rest };
  delete safeRest.dangerouslySetInnerHTML;

  return <div {...safeRest}>{renderSafeHtml(html)}</div>;
};

SafeHtmlContent.propTypes = {
  html: PropTypes.string,
};

export default SafeHtmlContent;
